import { Community } from "@models/user"
import axios from "axios"
import qs from "qs"
import type { Role } from "src/UserService"

export type CompanyProfile = "MANAGER" | "MEMBER"

export interface NamedCommunity {
  communityId: string
  communityName: string
  communityProfile: "GATED_COMMUNITY_MEMBER" | "GATED_COMMUNITY_ADMIN"
}

export interface CommunityMembershipRequest {
  communityId: string
  companyProfile: CompanyProfile
}

export interface UpdateMemberRequest {
  firstName: string
  lastName: string
  email: string
  companyProfile: CompanyProfile
  roles: Role[]
  communities: CommunityMembershipRequest[]
  phoneNumber?: string
}
export interface CompanyMember {
  userId: string
  email: string
  firstName: string
  lastName: string
  profile: CompanyProfile | null
  roles: Role[]
  communities: NamedCommunity[]
  isPending?: boolean
  phoneNumber?: string
}

export type CompanyMembersSortBy = "FIRST_NAME" | "LAST_NAME" | "EMAIL" | "PROFILE"
export type SortDirection = "ASCENDING" | "DESCENDING"

export interface PagedCompanyMembers {
  results: CompanyMember[]
  totalCount: number
}

export interface CompanyMembersParams {
  limit?: number
  offset?: number
  sortBy?: CompanyMembersSortBy
  sortDirection?: SortDirection
  query?: string
  roles?: Role[]
  communityIds?: string[]
}

export function getCompanyMembers(companyId: string, params: CompanyMembersParams = {}) {
  const {
    limit = 10,
    offset = 0,
    sortBy = "LAST_NAME",
    sortDirection = "ASCENDING",
    query,
    roles,
    communityIds
  } = params
  return axios
    .get<{ results: CompanyMember[]; totalCount: number }>(`/api/wfl/companies/${companyId}/members`, {
      params: { limit, offset, sortBy, sortDirection, query, roles, communityIds },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" })
    })
    .then((r) => ({
      results: r.data.results.map((m) => ({
        ...m,
        communities: m.communities
      })),
      totalCount: r.data.totalCount
    }))
}

interface Invitation {
  id: string
  companyId: string
  email: string
  roles: Role[]
  communityIds: string[]
}

export function getInvitations(companyId: string, userCommunities: Community[]): Promise<CompanyMember[]> {
  return axios.get<Invitation[]>(`/api/admin/invitations?companyId=${companyId}`).then((r) =>
    r.data.map((m) => ({
      ...m,
      userId: m.id,
      firstName: "-",
      lastName: "-",
      profile: null,
      communities: m.communityIds.map((c) => ({
        communityId: c,
        communityName: userCommunities.find((uc) => uc.gatedCommunityId === c)?.gatedCommunityName ?? "",
        communityProfile: "GATED_COMMUNITY_MEMBER" as const
      })),
      isPending: true
    }))
  )
}

export function inviteTeamMember(
  companyId: string,
  emails: string[],
  roles: Role[] = [],
  communityIds: string[] = []
): Promise<Invitation> {
  return axios
    .post<Invitation>(`/api/admin/invitations`, { companyId, emails, roles, communityIds })
    .then((r) => r.data)
}

export function deleteInvitation(id: string): Promise<void> {
  return axios.delete(`/api/admin/invitations/${id}`).then((r) => r.data)
}

export function resendInvitation(id: string): Promise<void> {
  return axios.put(`/api/admin/invitations/${id}`).then((r) => r.data)
}

export function updateCompanyMember(companyId: string, memberId: string, companyProfile: CompanyProfile) {
  return axios
    .put<void>(`/api/wfl/companies/${companyId}/members/${memberId}/profile`, { companyProfile })
    .then((r) => r.data)
}

export function deactivateCompanyMember(companyId: string, memberId: string): Promise<void> {
  return axios.delete(`/api/wfl/companies/${companyId}/members/${memberId}`).then((r) => r.data)
}

export function getCompanyMember(companyId: string, memberId: string): Promise<CompanyMember> {
  return axios.get<CompanyMember>(`/api/wfl/companies/${companyId}/members/${memberId}`).then((r) => r.data)
}

export function updateMember(companyId: string, memberId: string, request: UpdateMemberRequest) {
  return axios.put(`/api/wfl/companies/${companyId}/members/${memberId}`, request).then((r) => r.data)
}
